import { evaluateDipSetup } from "./dipChecklist.js";

import { refreshSetupLane } from "./scanLanes.js";

import { buildLimitingReasonLine } from "./setupReasonLine.js";
import { attachMovingAverages } from "./movingAverages.js";



const TOP_SIGNALS = new Set(["TOP_CONFIRMED", "TOP_FORMING"]);

const BOTTOM_SIGNALS = new Set(["BOTTOM_CONFIRMED", "BOTTOM_FORMING"]);



function moduleSignal(result) {

  return (

    result.signal ??

    result.moduleSignal ??

    result.signalSystem?.signal ??

    ""

  );

}



function gradeSuffix(grade) {

  return grade ? ` · ${grade}` : "";

}



function momentumLabel(checklist) {

  const g = gradeSuffix(checklist?.grade);

  if (checklist?.status === "ENTRY_OK") {

    return { label: `✅ CALLS — ENTRY OK${g}`, tone: "go", reason: "", sortRank: 100 };

  }

  if (checklist?.status === "SETUP") {

    return {

      label: `🟡 CALLS SETUP — waiting${g}`,

      tone: "pending",

      reason: checklist.reason || "",

      sortRank: 55,

    };

  }

  if (checklist?.status === "FORMING") {

    return {

      label: "🔵 CALLS FORMING",

      tone: "watch",

      reason: checklist.reason || "",

      sortRank: 48,

    };

  }

  return null;

}



function putsLabel(checklist) {

  const g = gradeSuffix(checklist?.grade);

  if (checklist?.status === "ENTRY_OK") {

    return { label: `✅ PUTS — ENTRY OK${g}`, tone: "go", reason: "", sortRank: 90 };

  }

  if (checklist?.status === "SETUP") {

    return {

      label: `🟡 PUTS SETUP — not confirmed${g}`,

      tone: "pending",

      reason: checklist.reason || "",

      sortRank: 40,

    };

  }

  if (checklist?.status === "FORMING") {

    return {

      label: "🔵 PUTS FORMING",

      tone: "watch",

      reason: checklist.reason || "",

      sortRank: 38,

    };

  }

  return null;

}



/** Legacy score-threshold labels (for scan-full diff report). */

export function resolveScannerActionThreshold(result) {

  const buyScore = result.buyScore ?? 0;

  const gate = result.gate?.label ?? "WAIT";

  const rr1 = result.rr1;

  if (buyScore >= 6 && gate === "GO" && rr1 != null && rr1 >= 1.5) {

    return "✅ CALLS — ENTRY OK";

  }

  if (buyScore >= 6) {

    if (gate === "BLOCKED") return "⛔ CALLS SETUP — BLOCKED";

    return "🟡 CALLS SETUP — waiting";

  }

  if (buyScore <= -6) {

    const puts = result.putsChecklist;

    if (puts?.allPass || puts?.status === "ENTRY_OK") return "✅ PUTS — ENTRY OK";

    return "🟡 PUTS SETUP — not confirmed";

  }

  if (buyScore <= -3) return "WATCH — BEARISH";

  if (buyScore >= 3) return "WATCH";

  return "WAIT";

}



export function resolveScannerAction(result) {

  const sig = moduleSignal(result);

  const buyScore = result.buyScore ?? 0;

  const gate = result.gate?.label ?? "WAIT";



  if (TOP_SIGNALS.has(sig)) {

    return {

      label: "⚠ TOP FORMING — avoid longs",

      tone: "warn",

      reason: result.gate?.failedCritical?.[0]?.shortLabel ?? gate,

      scoreGrey: true,

      scoreSuffix: "(top override)",

      sortRank: 20,

    };

  }



  if (sig === "BOTTOM_CONFIRMED") {

    return {

      label: "🔵 BOTTOM CONFIRMED",

      tone: "bottom",

      reason: gate,

      sortRank: 85,

    };

  }



  const dip = result.dipChecklist ?? evaluateDipSetup(result, result.marketCtx ?? {});

  if (!result.dipChecklist) result.dipChecklist = dip;



  const mom = result.momentumChecklist;

  const momAction = mom ? momentumLabel(mom) : null;

  if (momAction?.tone === "go") {

    return { ...momAction, lane: "MOMENTUM" };

  }



  if (dip.status === "TRIGGERED" && dip.actionable) {

    return {

      label: "✅ DIP — TRIGGERED",

      tone: "go",

      reason: "",

      sortRank: 92,

      lane: "DIP",

    };

  }

  if (dip.status === "TRIGGERED" || dip.status === "SETUP") {

    const pending = dip.status === "SETUP" || !dip.actionable;

    return {

      label: pending ? "🟡 DIP SETUP — wait for turn" : "✅ DIP — TRIGGERED",

      tone: pending ? "pending" : "go",

      reason: "",

      sortRank: pending ? 58 : 92,

      lane: "DIP",

    };

  }



  if (momAction) {

    if (momAction.tone === "pending" && gate === "BLOCKED") {

      return {

        label: "⛔ CALLS SETUP — BLOCKED",

        tone: "blocked",

        reason: result.gate?.failedCritical?.[0]?.shortLabel ?? "Blocked",

        sortRank: 5,

      };

    }

    return { ...momAction, lane: "MOMENTUM" };

  }



  const puts = result.putsChecklist;

  const putsAction = puts ? putsLabel(puts) : null;

  if (putsAction?.tone === "go") {

    return { ...putsAction, lane: "PUTS" };

  }

  if (putsAction) {

    return { ...putsAction, lane: "PUTS" };

  }



  if (buyScore <= -3) {

    return {

      label: "WATCH — BEARISH",

      tone: "watch",

      reason: "",

      sortRank: 25,

    };

  }

  if (buyScore >= 3) {

    return {

      label: "WATCH",

      tone: "watch",

      reason: "",

      sortRank: 35,

    };

  }



  return {

    label: "WAIT",

    tone: "muted",

    reason: "",

    sortRank: 10,

  };

}



function applyReasonLine(result, resolved) {

  const line = buildLimitingReasonLine(result, resolved);

  resolved.reason = line;

  return resolved;

}



export function attachScannerAction(result) {

  let resolved = resolveScannerAction(result);

  resolved = applyReasonLine(result, resolved);

  result.scannerAction = resolved;

  result.actionResolved = resolved.label;

  result.reasonLine = resolved.reason;

  refreshSetupLane(result);

  return resolved;

}



/** Current engine output — cached rows may omit scoringVersion until re-saved. */

export function isScoringV2Result(result) {

  if (!result || typeof result !== "object") return false;

  if (result.scoringVersion === "v2") return true;

  if (result.scoringVersion === "v1") return false;

  return result.gate != null && typeof result.buyScore === "number";

}



/** In-place: tag v2 + attach scannerAction for watchlist/scanner UI parity. */

export function normalizeScanResult(result) {

  if (!isScoringV2Result(result)) return result;

  if (result.scoringVersion !== "v2") result.scoringVersion = "v2";

  if (!result.ma) attachMovingAverages(result);
  if (!result.scannerAction) attachScannerAction(result);

  return result;

}



/** Map v2 scanner action → watchlist / shared display shape (same as Market Scanner). */

export function toneToVerdictStyle(tone) {

  if (tone === "go" || tone === "bottom") return "bullish";

  if (tone === "blocked") return "blocked";

  if (tone === "warn" || tone === "pending") return "warning";

  return "neutral";

}



/** Compact watchlist Action column — Signal column carries the full scanner label. */

export function v2WatchlistActionShort(result) {

  const a = result?.scannerAction ?? resolveScannerAction(result);

  const gate = result?.gate?.label ?? "—";

  const rr = result?.rr1;

  const reason = a.reason || "";

  if (a.tone === "go") {

    return { text: "✅ ENTRY OK", tip: reason ? `${a.label} — ${reason}` : a.label, tone: "go" };

  }

  if (a.tone === "blocked") {

    return { text: "⛔ BLOCKED", tip: reason || a.label, tone: "blocked" };

  }

  if (a.tone === "pending") {

    let text = gate !== "—" ? `Gate ${gate}` : "Not entry-ready";

    if (rr != null && rr < 1.5) text += ` · R:R ${rr.toFixed(1)}:1`;

    return { text, tip: reason || a.label, tone: "pending" };

  }

  if (a.tone === "warn") {

    return { text: "Avoid longs", tip: reason || a.label, tone: "warn" };

  }

  if (/DIP/i.test(a.label)) {

    return {

      text: a.label.includes("TRIGGERED") ? "✅ DIP TRIGGERED" : "Dip setup",

      tip: reason || a.label,

      tone: a.tone,

    };

  }

  if (/PUTS/i.test(a.label)) {

    return { text: a.label.includes("ENTRY OK") ? "✅ PUTS OK" : "Puts setup", tip: reason || a.label, tone: a.tone };

  }

  if (/FORMING/i.test(a.label)) {

    return { text: "Forming", tip: reason || a.label, tone: "watch" };

  }

  return { text: gate !== "—" ? `Gate ${gate}` : a.label, tip: reason || a.label, tone: a.tone || "muted" };

}



export function mapScannerActionToDisplay(result, opts = {}) {

  const score = typeof result?.buyScore === "number" ? result.buyScore : null;

  const a = result.scannerAction ?? resolveScannerAction(result);

  const verdict = a.label;

  const subtitle = a.reason || "";

  const showCallsBadge = /CALLS/i.test(verdict) && !/BLOCKED/i.test(verdict);

  const showPutsBadge = /PUTS/i.test(verdict);

  return {

    verdict,

    verdictStyle: toneToVerdictStyle(a.tone),

    subtitle,

    actionText: verdict,

    showCallsBadge,

    showPutsBadge,

    holdingText: null,

    showExitSignal: false,

    exitLabel: null,

    scoreDisplay: score,

    scoreGrey: !!a.scoreGrey,

    scoreSuffix: a.scoreSuffix || null,

    signalStrength: result?.signalStrength ?? null,

    _opts: opts,

  };

}


